import { useEffect, useState } from 'react'

import useBrazeIntegration from '@context/useBrazeIntegration'
import boxConfigurations from '@data/boxConfigurations.json'

import { filterBonusGroups, getBonusItemSelections } from '@lib/customization/bonus-items'
import { CUSTOMIZATION_CYCLE_KEY } from '@lib/customization/constants'
import { getProductSamplesWithCollectionLikeFormat } from '@lib/operations/product'
import type { ShopifyBasicOrder } from '@lib/queries/orders/get-basic-orders-query'
import type { ApiResponse } from '@lib/types/api'
import type { BoxConfiguration } from '@lib/types/box-configuration'
import type { CustomizationSelectionType, SingleSelectionType } from '@lib/types/customization'

export type GetCustomizationReturn = {
	selections: CustomizationSelectionType[]
}

interface CustomizationStep {
	name: string
	collection_slug: string
	max_items: number
}

export default function useCustomization(
	customizationType: 'NMC' | 'RMC',
	subscriptionProductSKU: string
) {
	const { brazeFunction } = useBrazeIntegration()
	const [loading, setLoading] = useState(false)
	const [reviewSubmitted, setReviewSubmitted] = useState(false)
	const [selections, setSelections] = useState<CustomizationSelectionType[]>([])
	const [bonusItemSelections, setBonusItemSelections] = useState<SingleSelectionType[]>([])
	const [bonusItemsInitialized, setBonusItemsInitialized] = useState(false)

	const boxConfiguration = (boxConfigurations as unknown as BoxConfiguration[]).find((config) =>
		config.product_skus.some((sku) => sku.sku === subscriptionProductSKU)
	)

	let allCustomizationGroups: any[] = []
	if (boxConfiguration) {
		allCustomizationGroups =
			customizationType === 'NMC'
				? boxConfiguration.nmc.customization_groups
				: boxConfiguration.rmc.customization_groups
	}

	// Filter out bonus items from regular customization steps
	const customizationSteps: CustomizationStep[] = filterBonusGroups(
		allCustomizationGroups as any[]
	) as CustomizationStep[]

	// Initialize selections with the correct number of steps (excluding bonus items)
	if (selections.length === 0 && customizationSteps.length > 0) {
		setSelections(
			customizationSteps.map((customizationStep) => ({
				selections: [],
				submitted: false,
				maxItems: customizationStep.max_items
			}))
		)
	}

	// Initialize bonus items when customization starts
	useEffect(() => {
		async function initializeBonusItems() {
			if (bonusItemsInitialized || !allCustomizationGroups.length) {
				return
			}

			try {
				setLoading(true)
				const bonusSelections = await getBonusItemSelections(
					allCustomizationGroups,
					customizationType
				)

				if (bonusSelections.length > 0) {
					console.log('Bonus items initialized:', bonusSelections)
					setBonusItemSelections(bonusSelections)
				}

				setBonusItemsInitialized(true)
			} catch (error) {
				console.error('Error initializing bonus items:', error)
			} finally {
				setLoading(false)
			}
		}

		initializeBonusItems()
	}, [customizationType, subscriptionProductSKU, bonusItemsInitialized])

	async function addSelectionToStep(
		step: number,
		newSelection: SingleSelectionType
	): Promise<void> {
		const newSelections = selections.map((selection, index: number) => {
			if (index === step) {
				const isProductAlreadySelected = selection.selections.find(
					(s) => s.product.id === newSelection.product.id
				)
				if (isProductAlreadySelected) {
					// Product is already selected, to override variant only
					const filteredSelections = selection.selections.filter(
						(s) => s.product.id !== newSelection.product.id
					)
					return {
						...selection,
						selections: [...filteredSelections, newSelection]
					}
				}
				// newSelection.product was not selected
				return { ...selection, selections: [...selection.selections, newSelection] }
			}
			return selection
		})
		console.log('updateStepSelection - newSelections:', newSelections)
		setSelections(newSelections)
	}

	async function deleteSelectionToStep(
		step: number,
		selectionToDelete: SingleSelectionType
	): Promise<void> {
		const newSelections = selections.map((selection, index: number) => {
			if (index === step)
				return {
					...selection,
					// return all the selected items but without the selectionToDelete item
					selections: selection.selections.filter(
						(sel) => sel.variant.id !== selectionToDelete.variant.id
					)
				}
			return selection
		})
		setSelections(newSelections)
	}

	async function submitSelections(orderId: string): Promise<void> {
		console.log('submitSelections - orderId:', orderId)
		setLoading(true)
		setReviewSubmitted(true)

		const selectionSubmit = selections
			.filter((s) => s.selections.length)
			.map((selection: CustomizationSelectionType) =>
				selection.selections.map((s) => ({
					variantId: s.variant?.id ?? '',
					variantSku: s.variant?.sku ?? ''
				}))
			)

		// Get bonus item IDs for order notes
		const bonusItemIds = bonusItemSelections.map((s) => s.variant.id)

		const res = await fetch(`/api/shopify/box-customization`, {
			method: 'POST',
			body: JSON.stringify({
				orderId,
				selections: selectionSubmit,
				bonusItemIds
			}),
			headers: {
				'Content-Type': 'application/json'
			}
		})
		const responseBody: ApiResponse = await res.json()

		if (responseBody.success) {
			console.log('selections submitted successfully - responseBody', responseBody)
			if (customizationType === 'NMC') {
				const braze = await brazeFunction()
				if (braze) {
					braze.logCustomEvent('nmc_customization_completed', { order_id: orderId })
					braze.requestImmediateDataFlush()
				}
			}
		} else {
			console.log('selections submitted with error - responseBody', responseBody)
		}
		setLoading(false)
	}

	async function submitStepSelections(orderId: string, stepIdx: number): Promise<void> {
		console.log('submitSelections - orderId:', orderId)
		setLoading(true)

		const selectionSubmit = selections[stepIdx].selections.map((s) => ({
			variantId: s.variant?.id ?? '',
			variantSku: s.variant?.sku ?? ''
		}))

		// Get bonus item IDs for order notes
		const bonusItemIds = bonusItemSelections.map((s) => s.variant.id)

		const res = await fetch(`/api/shopify/box-partial-customization`, {
			method: 'POST',
			body: JSON.stringify({
				orderId,
				selections: selectionSubmit,
				customizationGroupId: stepIdx,
				customizationType,
				bonusItemIds
			}),
			headers: {
				'Content-Type': 'application/json'
			}
		})
		const responseBody: ApiResponse = await res.json()

		if (responseBody.success) {
			console.log('selections submitted successfully - responseBody', responseBody)
		} else {
			console.log('selections submitted with error - responseBody', responseBody)
		}
		setSelections((prevSelections) =>
			prevSelections.map((step, index) => (index === stepIdx ? { ...step, submitted: true } : step))
		)
		setLoading(false)
	}

	async function submitBonusItems(orderId: string): Promise<void> {
		if (bonusItemSelections.length === 0) {
			return
		}

		console.log('submitBonusItems - orderId:', orderId)
		setLoading(true)

		const bonusItemIds = bonusItemSelections.map((s) => s.variant.id)

		const res = await fetch(`/api/shopify/box-partial-customization`, {
			method: 'POST',
			body: JSON.stringify({
				orderId,
				selections: [],
				customizationGroupId: -1, // Special flag for bonus items only
				customizationType,
				bonusItemIds
			}),
			headers: {
				'Content-Type': 'application/json'
			}
		})
		const responseBody: ApiResponse = await res.json()

		if (responseBody.success) {
			console.log('bonus items submitted successfully - responseBody', responseBody)
		} else {
			console.log('bonus items submitted with error - responseBody', responseBody)
		}
		setLoading(false)
	}

	async function confirmSelections(orderId: string): Promise<void> {
		console.log('submitSelections - orderId:', orderId)
		setLoading(true)
		setReviewSubmitted(true)

		const res = await fetch(`/api/shopify/box-confirm-customization`, {
			method: 'POST',
			body: JSON.stringify({ orderId, customizationType }),
			headers: {
				'Content-Type': 'application/json'
			}
		})
		const responseBody: ApiResponse = await res.json()

		if (responseBody.success) {
			// console.log('selections submitted successfully - responseBody', responseBody) 
			if (customizationType === 'NMC') {
				const braze = await brazeFunction()
				if (braze) {
					braze.logCustomEvent('nmc_customization_completed', { order_id: orderId })
					braze.requestImmediateDataFlush()
				}
			}
		} else {
			console.log('selections submitted with error - responseBody', responseBody)
		}
		setLoading(false)
	}

	/**
	 * Normalize order id for cycle comparison (GID vs numeric).
	 */
	function normalizeOrderIdForCycle(id: string): string {
		return id.startsWith('gid://') ? id : `gid://shopify/Order/${id}`
	}

	/**
	 * Restore selections from custom attributes.
	 * If orderId is provided and customization_cycle does not match this order, does not restore
	 * and returns hasStaleAttributes so the caller can clear them from the order.
	 * @param customAttributes - Shopify basic order custom attributes
	 * @param orderId - Current order id (GID or numeric); when provided, cycle is enforced
	 * @returns didRestore: true if selections were restored; hasStaleAttributes: true if attributes belong to another order and should be cleared
	 */
	async function restoreSelectionsFromCustomAttributes(
		customAttributes: ShopifyBasicOrder['customAttributes'],
		orderId?: string
	): Promise<{ didRestore: boolean; hasStaleAttributes?: boolean }> {
		setLoading(true)
		if (orderId) {
			const cycleValue = customAttributes.find((a) => a.key === CUSTOMIZATION_CYCLE_KEY)?.value?.trim()
			const hasCustomizationAttrs = customAttributes.some(
				(a) => a.key.startsWith('c_group_') || a.key === 'bonus_item_ids'
			)
			// Stale: cycle missing/empty but we have c_group_* or bonus_item_ids (legacy or corrupted)
			if (hasCustomizationAttrs && !cycleValue) {
				setLoading(false)
				return { didRestore: false, hasStaleAttributes: true }
			}
			// Stale: cycle present but belongs to a different order
			if (cycleValue) {
				const normalizedOrderId = normalizeOrderIdForCycle(orderId)
				const normalizedCycle = normalizeOrderIdForCycle(cycleValue)
				if (normalizedCycle !== normalizedOrderId) {
					setLoading(false)
					return { didRestore: false, hasStaleAttributes: true }
				}
			}
		}
		const customizationAttributes = customAttributes.filter((attr) =>
			attr.key.startsWith('c_group_')
		)

		// Get bonus item IDs from custom attributes
		const bonusItemIdsAttr = customAttributes.find((attr) => attr.key === 'bonus_item_ids')
		const bonusItemIds = bonusItemIdsAttr ? bonusItemIdsAttr.value.split(',') : []

		const allUniqueVariantIds = [
			...new Set([
				...customizationAttributes.map((attr) => attr.value.split(',')).flat(),
				...bonusItemIds
			])
		].filter((id) => !!id)

		const getAllProducts = await getProductSamplesWithCollectionLikeFormat(
			allUniqueVariantIds,
			customizationType
		)

		// Restore bonus items
		const bonusSelections: SingleSelectionType[] = bonusItemIds
			.map((variantId) => {
				const product = getAllProducts.find((p) => p.sampleVariants.some((v) => v.id === variantId))
				if (!product) return null
				const variant = product.sampleVariants.find((v) => v.id === variantId)
				if (!variant) return null
				return {
					product,
					variant,
					isBonus: true
				}
			})
			.filter((s) => !!s) as SingleSelectionType[]

		if (bonusSelections.length > 0) {
			setBonusItemSelections(bonusSelections)
		}

		const updatedSelections = customizationAttributes.reduce(
			(acc, attr) => {
				const match = attr.key.match(/^c_group_(\d+)$/)
				if (match) {
					const groupIndex = parseInt(match[1], 10)
					if (groupIndex >= 0 && groupIndex < acc.length) {
						const variantIds = attr.value.split(',')
						const restoredSelections = variantIds
							.map((variantId) => {
								const product = getAllProducts.find((p) =>
									p.sampleVariants.some((v) => v.id === variantId)
								)
								if (!product) return null
								const variant = product.sampleVariants.find((v) => v.id === variantId)
								if (!variant) return null
								return {
									product,
									variant
								}
							})
							.filter((s) => !!s) as SingleSelectionType[]

						acc[groupIndex] = {
							...acc[groupIndex],
							selections: restoredSelections,
							submitted: true
						}
					}
				}
				return acc
			},
			[...selections]
		)

		setSelections(updatedSelections)
		setLoading(false)
		return { didRestore: true }
	}
	// Get bonus item configuration (tag text, color, tooltip)
	let bonusItemConfig = {
		tagText: 'Free Gift!',
		tagColor: '#10b981',
		tooltipText: undefined as any
	}
	if (boxConfiguration) {
		if (customizationType === 'NMC') {
			bonusItemConfig = {
				tagText: boxConfiguration.nmc.bonus_item_tag_text || 'Free Gift!',
				tagColor: boxConfiguration.nmc.bonus_item_tag_color || '#10b981',
				tooltipText: boxConfiguration.nmc.bonus_item_tooltip_text || undefined
			}
		} else {
			bonusItemConfig = {
				tagText: boxConfiguration.rmc.bonus_item_tag_text || 'Free Gift!',
				tagColor: boxConfiguration.rmc.bonus_item_tag_color || '#10b981',
				tooltipText: boxConfiguration.rmc.bonus_item_tooltip_text || undefined
			}
		}
	}

	if (!boxConfiguration) {
		console.error(`No box configuration found for SKU: ${subscriptionProductSKU}`)
		return {
			selections,
			bonusItemSelections,
			bonusItemConfig,
			addSelectionToStep: async () => {},
			deleteSelectionToStep: async () => {},
			submitSelections: async () => {},
			loading,
			reviewSubmitted,
			submitStepSelections: async () => {},
			confirmSelections: async () => {},
			restoreSelectionsFromCustomAttributes: async () => ({
				didRestore: false,
				hasStaleAttributes: false
			}),
			submitBonusItems: async () => {}
		}
	}

	return {
		selections,
		bonusItemSelections,
		bonusItemConfig,
		addSelectionToStep,
		deleteSelectionToStep,
		submitSelections,
		loading,
		reviewSubmitted,
		submitStepSelections,
		confirmSelections,
		restoreSelectionsFromCustomAttributes,
		submitBonusItems
	}
}
